import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate } from 'react-router-dom';
import { CreditCard, Copy, Package, Check } from 'lucide-react';
import { cardsAPI } from '../services/api';
import { toast } from 'sonner';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';

const CardDetails = () => {
  const { cardId } = useParams();
  const navigate = useNavigate();
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDetails, setShowDetails] = useState(false);
  const [requestPhysicalOpen, setRequestPhysicalOpen] = useState(false);
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [requesting, setRequesting] = useState(false);

  useEffect(() => {
    loadCard();
  }, [cardId]);

  const loadCard = async () => {
    try {
      const response = await cardsAPI.getMyCards();
      const foundCard = response.data.find(c => c.id === cardId);
      if (!foundCard) {
        toast.error('Card not found');
        navigate('/cards');
        return;
      }
      setCard(foundCard);
    } catch (error) {
      toast.error('Failed to load card');
      navigate('/cards');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const handleRequestPhysical = async (e) => {
    e.preventDefault();
    setRequesting(true);

    try {
      await cardsAPI.requestPhysical({ card_id: cardId, delivery_address: deliveryAddress });
      toast.success('Physical card requested! Expected delivery: 7-10 business days');
      setRequestPhysicalOpen(false);
      loadCard();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Request failed');
    } finally {
      setRequesting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-white text-xl font-secondary">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="space-y-8"
        >
          {/* Header */}
          <div>
            <h1 className="text-4xl font-primary font-bold text-white mb-2">{card.tier.toUpperCase()} CARD</h1>
            <p className="text-gray-400 font-secondary">Virtual card details</p>
          </div>

          {/* Card Display */}
          <div className="glass-card p-8 rounded-2xl">
            <div className={`relative h-64 rounded-2xl p-8 overflow-hidden mb-8 ${
              card.tier === 'silver' ? 'gradient-card-silver' : 'gradient-card-gold'
            }`}>
              <div className="relative z-10 h-full flex flex-col justify-between">
                <div className="flex items-center justify-between">
                  <CreditCard className="w-12 h-12 text-black/70" />
                  <div className="text-black/70 font-primary font-bold uppercase">{card.tier} CARD</div>
                </div>
                <div>
                  <div className="text-black/70 font-mono text-2xl tracking-wider mb-4">
                    {showDetails ? card.card_number : card.card_number.replace(/\d(?=\d{4})/g, '•')}
                  </div>
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-black/70 text-xs mb-1 opacity-70">CARDHOLDER</div>
                      <div className="text-black/70 font-secondary font-bold">{card.holder_name}</div>
                    </div>
                    <div>
                      <div className="text-black/70 text-xs mb-1 opacity-70">EXPIRES</div>
                      <div className="text-black/70 font-mono font-bold">{card.expiry}</div>
                    </div>
                    <div>
                      <div className="text-black/70 text-xs mb-1 opacity-70">CVV</div>
                      <div className="text-black/70 font-mono font-bold">{showDetails ? card.cvv : '•••'}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <Button
              onClick={() => setShowDetails(!showDetails)}
              className="w-full bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-xl py-4 mb-4"
              data-testid="toggle-card-details-btn"
            >
              {showDetails ? 'Hide Details' : 'Show Details'}
            </Button>

            {showDetails && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="space-y-3"
              >
                <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                  <div>
                    <div className="text-gray-400 text-sm mb-1">Card Number</div>
                    <div className="text-white font-mono">{card.card_number}</div>
                  </div>
                  <button
                    onClick={() => copyToClipboard(card.card_number, 'Card number')}
                    className="text-[#00F0FF] hover:text-[#00C2CC] transition-colors"
                    data-testid="copy-card-number-btn"
                  >
                    <Copy className="w-5 h-5" />
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                    <div>
                      <div className="text-gray-400 text-sm mb-1">Expiry</div>
                      <div className="text-white font-mono">{card.expiry}</div>
                    </div>
                    <button
                      onClick={() => copyToClipboard(card.expiry, 'Expiry date')}
                      className="text-[#00F0FF] hover:text-[#00C2CC] transition-colors"
                      data-testid="copy-expiry-btn"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-white/5 rounded-xl">
                    <div>
                      <div className="text-gray-400 text-sm mb-1">CVV</div>
                      <div className="text-white font-mono">{card.cvv}</div>
                    </div>
                    <button
                      onClick={() => copyToClipboard(card.cvv, 'CVV')}
                      className="text-[#00F0FF] hover:text-[#00C2CC] transition-colors"
                      data-testid="copy-cvv-btn"
                    >
                      <Copy className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Card Status */}
          <div className="glass-card p-6 rounded-2xl">
            <h3 className="text-xl font-primary font-bold text-white mb-4">CARD STATUS</h3>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-gray-400 font-secondary">Status</span>
                <span className="text-[#00FF94] font-semibold uppercase">{card.status}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-gray-400 font-secondary">Physical Card</span>
                <span className="text-white font-semibold">{card.physical_requested ? 'Requested' : 'Not Requested'}</span>
              </div>
            </div>
          </div>

          {/* Request Physical Card */}
          {!card.physical_requested && (
            <Dialog open={requestPhysicalOpen} onOpenChange={setRequestPhysicalOpen}>
              <DialogTrigger asChild>
                <Button className="w-full bg-[#7000FF] hover:bg-[#5B00CC] text-white rounded-full py-6 text-lg font-bold neon-glow-primary" data-testid="request-physical-card-btn">
                  <Package className="w-5 h-5 mr-2" />
                  Request Physical Card
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#0A051E] border-white/10 text-white">
                <DialogHeader>
                  <DialogTitle className="font-primary text-2xl">REQUEST PHYSICAL CARD</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleRequestPhysical} className="space-y-4 mt-4">
                  <div>
                    <Label className="text-gray-300 mb-2 block">Delivery Address</Label>
                    <Textarea
                      placeholder="Enter your full delivery address..."
                      value={deliveryAddress}
                      onChange={(e) => setDeliveryAddress(e.target.value)}
                      className="bg-white/5 border-white/10 text-white placeholder:text-gray-600 min-h-[120px]"
                      required
                      data-testid="delivery-address-input"
                    />
                  </div>

                  <div className="bg-white/5 border border-white/10 rounded-xl p-4 text-sm text-gray-300">
                    <div className="flex items-start gap-2">
                      <Check className="w-5 h-5 text-[#00FF94] flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold text-white mb-1">Free Delivery</p>
                        <p>Your physical card will be delivered within 7-10 business days</p>
                      </div>
                    </div>
                  </div>

                  <Button
                    type="submit"
                    disabled={requesting}
                    className="w-full bg-[#7000FF] hover:bg-[#5B00CC] text-white rounded-full py-6"
                    data-testid="confirm-physical-card-request-btn"
                  >
                    {requesting ? 'Processing...' : 'Confirm Request'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}

          {card.physical_requested && (
            <div className="glass-card p-6 rounded-2xl">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-[#00FF94]/20 flex items-center justify-center flex-shrink-0">
                  <Package className="w-6 h-6 text-[#00FF94]" />
                </div>
                <div>
                  <h3 className="text-lg font-primary font-bold text-white mb-2">PHYSICAL CARD REQUESTED</h3>
                  <p className="text-gray-400 font-secondary">Your physical card has been requested and will be delivered within 7-10 business days.</p>
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </Layout>
  );
};

export default CardDetails;
